import React, { useState, useEffect } from 'react';
import { AdminSidebar } from './AdminDashboard';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABELS = {
  starter: 'Esencial',
  pro: 'Profesional',
  premium: 'Empresarial'
};

const PAYMENT_STATUS_COLORS = {
  pending: 'bg-yellow-500',
  completed: 'bg-green-500',
  failed: 'bg-red-500',
  expired: 'bg-zinc-500'
};

const PAYMENT_STATUS_LABELS = {
  pending: 'Pendiente',
  completed: 'Completado',
  failed: 'Fallido',
  expired: 'Expirado'
};

export default function AdminOrders({ user }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchOrders();
  }, []);
  
  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/api/orders`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    return (
      <Badge className={`${PAYMENT_STATUS_COLORS[status] || 'bg-zinc-500'} text-white hover:opacity-80`}>
        {PAYMENT_STATUS_LABELS[status] || status}
      </Badge>
    );
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <AdminSidebar user={user} />
      
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" data-testid="orders-title">Órdenes</h1>
          <p className="text-zinc-500 mt-1">Gestiona las órdenes de compra</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/5 p-12 text-center">
            <p className="text-zinc-500">No hay órdenes todavía</p>
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-white/5">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400">ID</TableHead>
                  <TableHead className="text-zinc-400">Plan</TableHead>
                  <TableHead className="text-zinc-400">Monto</TableHead>
                  <TableHead className="text-zinc-400">Método</TableHead>
                  <TableHead className="text-zinc-400">Fecha</TableHead>
                  <TableHead className="text-zinc-400">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.order_id} className="border-white/5" data-testid={`order-row-${order.order_id}`}>
                    <TableCell className="font-mono text-sm text-zinc-400">
                      {order.order_id.slice(-8)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {PLAN_LABELS[order.plan] || order.plan}
                    </TableCell>
                    <TableCell className="font-bold">
                      ${order.amount} {order.currency}
                    </TableCell>
                    <TableCell className="text-zinc-400 capitalize">
                      {order.payment_method}
                    </TableCell>
                    <TableCell className="text-zinc-400">
                      {formatDate(order.created_at)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(order.payment_status)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        
        {/* Summary Cards */}
        {orders.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-[#0A0A0A] border border-white/5 p-6">
              <p className="text-sm text-zinc-500 mb-2">Total de Órdenes</p>
              <p className="text-3xl font-bold">{orders.length}</p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/5 p-6">
              <p className="text-sm text-zinc-500 mb-2">Órdenes Completadas</p>
              <p className="text-3xl font-bold text-green-500">
                {orders.filter(o => o.payment_status === 'completed').length}
              </p>
            </div>
            <div className="bg-[#0A0A0A] border border-white/5 p-6">
              <p className="text-sm text-zinc-500 mb-2">Ingresos Totales</p>
              <p className="text-3xl font-bold">
                ${orders
                  .filter(o => o.payment_status === 'completed')
                  .reduce((sum, o) => sum + o.amount, 0)
                  .toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
